/**
 * Сколько живёт стенд — «38 мин», «2 ч 14 мин», «4 д 11 ч».
 *
 * Две единицы, не больше: точность до минуты у стенда, поднятого четыре дня
 * назад, никому не нужна, а «4 д 11 ч 02 мин» в колонке шириной 76 не влезает.
 */
const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export interface UptimeLabels {
  /** Сокращения единиц: «д», «ч», «мин» */
  days: string
  hours: string
  minutes: string
}

export const formatUptime = (
  iso: string,
  labels: UptimeLabels,
  now = new Date(),
): string => {
  if (!iso) return ''

  const started = new Date(iso)

  if (Number.isNaN(started.getTime())) return ''

  return formatSpan(now.getTime() - started.getTime(), labels)
}

/** Промежуток теми же двумя единицами: аптайм и «сносится через» считаются одинаково. */
export const formatSpan = (milliseconds: number, labels: UptimeLabels): string => {
  const elapsed = Math.max(0, milliseconds)

  if (elapsed >= DAY) {
    const days = Math.floor(elapsed / DAY)
    const hours = Math.floor((elapsed % DAY) / HOUR)

    return `${days} ${labels.days} ${hours} ${labels.hours}`
  }

  if (elapsed >= HOUR) {
    const hours = Math.floor(elapsed / HOUR)
    // Минуты с ведущим нулём: колонка выравнивается моноширинным шрифтом, и
    // «2 ч 4 мин» рядом с «2 ч 14 мин» прыгает.
    const minutes = String(Math.floor((elapsed % HOUR) / MINUTE)).padStart(2, '0')

    return `${hours} ${labels.hours} ${minutes} ${labels.minutes}`
  }

  return `${Math.floor(elapsed / MINUTE)} ${labels.minutes}`
}

/**
 * Инициалы вместо имени — «Михаил Кузнецов» → «МК».
 *
 * Так в макете: колонка «поднял» шириной 40, и полное имя в неё не входит ни в
 * одной из трёх локалей. Кто именно — видно по наведению.
 */
export const initials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

/** Простаивает ли стенд: граница из макета — трое суток без дела. */
export const IDLE_AFTER_DAYS = 3

export const isIdle = (iso: string, now = new Date()): boolean => {
  if (!iso) return false

  const started = new Date(iso)

  if (Number.isNaN(started.getTime())) return false

  return now.getTime() - started.getTime() >= IDLE_AFTER_DAYS * DAY
}

/**
 * Короткое имя репозитория — `vinprop/frontend/crm-vue` → `crm-vue`.
 *
 * Чипами состава стенд подписан в макете именно так: группа у всех наших
 * репозиториев одна, и полный путь в чипе — это три четверти одинакового
 * текста на строку.
 */
export const shortService = (path: string): string => path.split('/').pop() ?? path

export type StandState = 'active' | 'building' | 'idle'

/**
 * Состояние стенда: собирается, работает или простаивает.
 *
 * «Собирается» — ни у одного сервиса ещё нет адреса: окружение GitLab заводит
 * уже на сборке, а `external_url` появляется только после выката.
 */
export const standStateOf = (
  stand: { services: { url: string }[]; raisedAt: string },
  now = new Date(),
): StandState => {
  if (!stand.services.some((service) => service.url)) return 'building'

  return isIdle(stand.raisedAt, now) ? 'idle' : 'active'
}
